import { useState, useRef, useEffect, createElement } from 'react';
// Webpack resolves this to a URL that works in Expo web dev and production
// @ts-ignore — image module resolved by bundler
import heroBackground from '../assets/hd_hero_1.jpeg';
import { Link, useSearchParams } from 'react-router-dom';
import { View, Text, StyleSheet, Platform, Pressable } from 'react-native';
import WebScrollView from '../components/WebScrollView';
import { shadows } from '../lib/styles';
import { useGooglePlacesAutocomplete } from '../hooks/useGooglePlacesAutocomplete';
import { useOnboardingForm } from '../hooks/useOnboardingForm';
import { useHomeEnrichment } from '../hooks/useHomeEnrichment';
import { useActiveHome } from '../contexts/ActiveHomeContext';
import { supabase } from '../lib/supabase';
import { sendNewUserRegistrationEmail } from '../lib/email';
import { acceptHomeInvitation } from '../lib/household';
import { SUPABASE_ANON_KEY } from '../lib/constants';
import { WelcomeStep } from '../components/onboarding/WelcomeStep';
import { AddressStep } from '../components/onboarding/AddressStep';
import { HomeDetailsStep } from '../components/onboarding/HomeDetailsStep';
import { InterestsStep } from '../components/onboarding/InterestsStep';
import { AccountCreationStep } from '../components/onboarding/AccountCreationStep';
import { SuccessStep } from '../components/onboarding/SuccessStep';
import { ProgressBar } from '../components/onboarding/ProgressBar';
import { NavigationButtons } from '../components/onboarding/NavigationButtons';
import { REFERRAL_CONFIG, formatCentsToUSDShort } from '../config/referral';
import {
  getPendingAgentIdentifier,
  setPendingAgentIdentifier,
  validateAgentIdentifier,
  linkReferringAgent,
  type ReferringAgent,
} from '../lib/referringAgent';

const ONBOARDING_STORAGE_KEY = 'onboarding_progress';
const ONBOARDING_HOME_ID_KEY = 'onboarding_home_id';

const Onboarding = () => {
  const { refreshHomes } = useActiveHome();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const addressInputRef = useRef<any>(null);
  
  // Referral state
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [referralValid, setReferralValid] = useState<boolean>(false);
  const [referrerName, setReferrerName] = useState<string>('');
  const [validatingReferral, setValidatingReferral] = useState(false);
  const [referralClaimFailed, setReferralClaimFailed] = useState(false);
  const [householdInviteToken, setHouseholdInviteToken] = useState<string | null>(null);

  // Referring real estate agent state. Populated from `?agent=<slug-or-code>`
  // URL param or sessionStorage seeded by the public /agent/:slug landing page.
  const [agentIdentifier, setAgentIdentifierState] = useState<string | null>(null);
  const [referringAgent, setReferringAgent] = useState<ReferringAgent | null>(null);

  // Form management hook
  const {
    formData,
    setFormData,
    updateField,
    updateFields,
    handleInterestToggle,
    resetForm
  } = useOnboardingForm();

  // Google Places autocomplete hook
  const {
    inputValue: addressInputValue,
    setInputValue: setAddressInputValue,
    suggestions: addressSuggestions,
    isLoading: isLoadingAddresses,
    selectedPlace,
    selectedPlaceDescription,
    error: addressError,
    selectPlace,
    clearSelection,
  } = useGooglePlacesAutocomplete({ debounceMs: 300, minLength: 3 });

  // Home enrichment hook
  const {
    enrichingHomeData,
    enrichmentError,
    enrichedData,
    createdHomeId,
    setCreatedHomeId,
    currentProgressMessage,
    fadeAnim,
    progressMessages,
    updateHomeForOnboarding,
    clearEnrichment
  } = useHomeEnrichment(selectedPlace, formData, setFormData);

  // Load progress from localStorage on mount
  useEffect(() => {
    try {
      const savedStep = localStorage.getItem(ONBOARDING_STORAGE_KEY);
      const savedHomeId = localStorage.getItem(ONBOARDING_HOME_ID_KEY);
      
      if (savedStep !== null) {
        const step = parseInt(savedStep, 10);
        if (!isNaN(step) && step >= 0 && step <= 5) {
          setCurrentStep(step);
        }
      }
      
      if (savedHomeId) {
        setCreatedHomeId(savedHomeId);
      }
    } catch (err) {
      console.error('Error loading onboarding progress:', err);
    }
  }, [setCreatedHomeId]);

  // Check for referral code in URL
  useEffect(() => {
    const code = searchParams.get('ref');
    const inviteToken = searchParams.get('invite');
    if (inviteToken) {
      setHouseholdInviteToken(inviteToken);
    }
    if (code) {
      setReferralCode(code);
      validateReferralCode(code);
    }
  }, [searchParams]);

  // Check for referring real estate agent (URL param wins, sessionStorage fallback).
  useEffect(() => {
    const fromUrl = searchParams.get('agent');
    const fromStorage = getPendingAgentIdentifier();
    const identifier = fromUrl || fromStorage;
    if (!identifier) return;

    if (fromUrl && fromUrl !== fromStorage) {
      setPendingAgentIdentifier(fromUrl);
    }
    setAgentIdentifierState(identifier);

    (async () => {
      const result = await validateAgentIdentifier(identifier);
      if (result.valid) {
        setReferringAgent(result.agent);
      } else {
        // Clear stale storage so an invalid value doesn't persist.
        setPendingAgentIdentifier(null);
      }
    })();
  }, [searchParams]);

  const validateReferralCode = async (code: string) => {
    try {
      setValidatingReferral(true);
      const response = await fetch(
        `${supabase.supabaseUrl}/functions/v1/validate-referral-code`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${SUPABASE_ANON_KEY || ''}`,
            'apikey': SUPABASE_ANON_KEY || '',
          },
          body: JSON.stringify({ code }),
        }
      );

      const result = await response.json();

      if (result.valid) {
        setReferralValid(true);
        setReferrerName(result.invitation.referrerName);
      } else {
        setReferralValid(false);
        console.warn('Invalid referral code:', result.error);
      }
    } catch (error) {
      console.error('Error validating referral code:', error);
      setReferralValid(false);
    } finally {
      setValidatingReferral(false);
    }
  };

  // Save step to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(ONBOARDING_STORAGE_KEY, currentStep.toString());
    } catch (err) {
      console.error('Error saving onboarding step:', err);
    }
  }, [currentStep]);

  // Save homeId to localStorage whenever it's set
  useEffect(() => {
    try {
      if (createdHomeId) {
        localStorage.setItem(ONBOARDING_HOME_ID_KEY, createdHomeId);
      }
    } catch (err) {
      console.error('Error saving onboarding home ID:', err);
    }
  }, [createdHomeId]);

  // Sync address input with formData (but don't sync when a place is selected)
  useEffect(() => {
    if (currentStep === 1 && !selectedPlace && formData.address !== addressInputValue) {
      setAddressInputValue(formData.address);
    }
  }, [currentStep, formData.address, addressInputValue, setAddressInputValue, selectedPlace]);

  // Update formData when a place is selected
  useEffect(() => {
    if (selectedPlace) {
      setFormData(prev => ({
        ...prev,
        address: selectedPlace.street_address,
        city: selectedPlace.city,
        state: selectedPlace.state,
        zipCode: selectedPlace.zip_code,
      }));
      setShowAddressSuggestions(false);
      setError('');
    }
  }, [selectedPlace, setFormData]);

  const handleAddressChange = (value: string) => {
    updateFields({
      address: value,
      // Clear city/state/zip if user is editing after selection
      ...(selectedPlace && { city: '', state: '', zipCode: '' })
    });
  };

  const       handleAddressClear = () => {
    updateFields({
      address: '',
      city: '',
      state: '',
      zipCode: '',
      bedrooms: '',
      bathrooms: '',
      squareFootage: '',
      yearBuilt: '',
      purchaseDate: ''
    });
    clearEnrichment();
  };

  const handleRestart = () => {
    try {
      // Clear localStorage
      localStorage.removeItem(ONBOARDING_STORAGE_KEY);
      localStorage.removeItem(ONBOARDING_HOME_ID_KEY);
      
      // Reset state
      setCurrentStep(0);
      setError('');
      setShowAddressSuggestions(false);
      
      // Clear form data
      resetForm();
      
      // Clear address selection
      clearSelection();
      
      // Clear enrichment
      clearEnrichment();
    } catch (err) {
      console.error('Error restarting onboarding:', err);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      if (!formData.acceptedTerms) {
        throw new Error('You must accept the Terms of Service and Privacy Policy to continue.');
      }

      // Create user account
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
      });

      if (authError) throw authError;

      const user = authData.user;
      if (!user) throw new Error('No user data returned');

      // Create profile with default home_manager
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          full_name: formData.fullName,
          phone_number: formData.phoneNumber,
          interests: formData.interests,
          current_services: formData.currentServices,
          desired_services: formData.desiredServices,
          home_manager: 'Brandon Takens',
          terms_accepted_at: new Date().toISOString()
        });

      if (profileError) throw profileError;

      // Create Stripe customer automatically
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.access_token) {
          const stripeResponse = await fetch(
            `${supabase.supabaseUrl}/functions/v1/create-stripe-customer-self-service`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${session.access_token}`,
              },
            }
          );

          const stripeResult = await stripeResponse.json();

          if (!stripeResponse.ok || !stripeResult.success) {
            console.error('Failed to create Stripe customer:', stripeResult.error);
            // Don't throw - allow signup to complete even if Stripe creation fails
            // Admin can manually create Stripe customer later if needed
          } else {
            console.log('Stripe customer created successfully:', stripeResult.stripe_customer_id);
          }
        }
      } catch (stripeError) {
        console.error('Error creating Stripe customer:', stripeError);
        // Don't throw - allow signup to complete even if Stripe creation fails
        // Admin can manually create Stripe customer later if needed
      }

      // Update existing home with owner_id, or create new one if home wasn't created earlier
      if (createdHomeId) {
        // Update the home we created earlier to assign the owner
        // Parse purchase date - convert YYYY-MM-DD string to date or null
        let purchaseDateValue: string | null = null;
        if (formData.purchaseDate && formData.purchaseDate.trim()) {
          const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
          if (dateRegex.test(formData.purchaseDate.trim())) {
            purchaseDateValue = formData.purchaseDate.trim();
          }
        }

        const { error: homeUpdateError } = await supabase
          .from('homes')
          .update({
            owner_id: user.id,
            // Update any fields that might have changed
            address: formData.address,
            city: formData.city,
            state: formData.state,
            zip_code: formData.zipCode,
            bedrooms: parseInt(formData.bedrooms) || null,
            bathrooms: parseFloat(formData.bathrooms) || null,
            square_footage: parseInt(formData.squareFootage) || null,
            year_built: parseInt(formData.yearBuilt) || null,
            purchase_date: purchaseDateValue,
            cover_photo_url: enrichedData?.cover_photo_url || null
          })
          .eq('id', createdHomeId);

        if (homeUpdateError) {
          console.error('Error updating home ownership:', homeUpdateError);
          // Log error but don't throw - allow user to continue
          // The home exists but is unclaimed, can be fixed later by admin or user
          // User can still access the app and add the home manually if needed
        }
      } else {
        // Fallback: create home if it wasn't created earlier (shouldn't happen in normal flow)
        // This handles edge cases like if user refreshed page or navigated away
        console.warn('No createdHomeId found, creating home as fallback');
        // Parse purchase date - convert YYYY-MM-DD string to date or null
        let purchaseDateValue: string | null = null;
        if (formData.purchaseDate && formData.purchaseDate.trim()) {
          const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
          if (dateRegex.test(formData.purchaseDate.trim())) {
            purchaseDateValue = formData.purchaseDate.trim();
          }
        }

        const { data: homeData, error: homeError } = await supabase
          .from('homes')
          .insert({
            owner_id: user.id,
            address: formData.address,
            city: formData.city,
            state: formData.state,
            zip_code: formData.zipCode,
            bedrooms: parseInt(formData.bedrooms) || null,
            bathrooms: parseFloat(formData.bathrooms) || null,
            square_footage: parseInt(formData.squareFootage) || null,
            year_built: parseInt(formData.yearBuilt) || null,
            purchase_date: purchaseDateValue,
            cover_photo_url: enrichedData?.cover_photo_url || null
          })
          .select('id')
          .single();

        if (homeError) {
          console.error('Error creating home:', homeError);
          // Log error but don't throw - allow user to continue
          // User can add home later from dashboard
        } else if (homeData?.id) {
          // Store the ID in case we need it later
          setCreatedHomeId(homeData.id);
        }
      }

      // Send new user registration email
      try {
        await sendNewUserRegistrationEmail({
          fullname: formData.fullName,
          email: formData.email,
          phoneNumber: formData.phoneNumber
        });
      } catch (emailError) {
        console.error('Error sending registration email:', emailError);
        // Don't throw - allow signup to complete even if email fails
      }

      // Claim referral if valid code exists
      if (referralCode && referralValid) {
        try {
          const { data: { session } } = await supabase.auth.getSession();

          const response = await fetch(
            `${supabase.supabaseUrl}/functions/v1/claim-referral`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${session?.access_token}`,
              },
              body: JSON.stringify({ code: referralCode }),
            }
          );

          const result = await response.json();

          if (!response.ok || !result.success) {
            // Surface the failure so we don't silently lose credits the way
            // the persistent-code unique-constraint bug did. Signup itself
            // still succeeds; the user sees a non-blocking notice on the
            // success step and can ping support to reconcile.
            console.error('[onboarding] Failed to claim referral', {
              status: response.status,
              code: referralCode,
              referrer: referrerName,
              error: result?.error,
            });
            setReferralClaimFailed(true);
          }
        } catch (referralError) {
          console.error('[onboarding] Error claiming referral', {
            code: referralCode,
            referrer: referrerName,
            error: referralError,
          });
          setReferralClaimFailed(true);
        }
      }

      // Link the homeowner to a referring real estate agent if one was
      // captured (via /agent/:slug landing page or ?agent= URL param).
      if (agentIdentifier) {
        try {
          const linkResult = await linkReferringAgent(agentIdentifier, 'signup');
          if (!linkResult.success) {
            console.error('Failed to link referring agent:', linkResult.error);
          }
        } catch (agentLinkError) {
          console.error('Error linking referring agent:', agentLinkError);
        } finally {
          setPendingAgentIdentifier(null);
        }
      }

      // Accept household invite if token was provided in URL
      if (householdInviteToken) {
        try {
          await acceptHomeInvitation(householdInviteToken);
        } catch (inviteError) {
          console.error('Error accepting household invite:', inviteError);
          // Keep signup success path even if invite acceptance fails.
        }
      }

      try {
        await refreshHomes();
      } catch (refreshError) {
        // Non-blocking: signup is complete even if active-home refresh fails.
        console.error('Error refreshing active homes after onboarding:', refreshError);
      }

      setCurrentStep(5);
      
      // Clear onboarding progress from localStorage on successful completion
      try {
        localStorage.removeItem(ONBOARDING_STORAGE_KEY);
        localStorage.removeItem(ONBOARDING_HOME_ID_KEY);
      } catch (err) {
        console.error('Error clearing onboarding progress:', err);
      }
    } catch (error: any) {
      console.error('Error during onboarding:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <WelcomeStep />;

      case 1:
        return (
          <AddressStep
            addressInputValue={addressInputValue}
            setAddressInputValue={setAddressInputValue}
            selectedPlace={selectedPlace}
            selectedPlaceDescription={selectedPlaceDescription}
            addressSuggestions={addressSuggestions}
            isLoadingAddresses={isLoadingAddresses}
            addressError={addressError}
            showAddressSuggestions={showAddressSuggestions}
            setShowAddressSuggestions={setShowAddressSuggestions}
            selectPlace={selectPlace}
            clearSelection={clearSelection}
            onAddressChange={handleAddressChange}
            onAddressClear={handleAddressClear}
            addressInputRef={addressInputRef}
            setError={setError}
          />
        );

      case 2:
        return (
          <HomeDetailsStep
            bedrooms={formData.bedrooms}
            bathrooms={formData.bathrooms}
            squareFootage={formData.squareFootage}
            yearBuilt={formData.yearBuilt}
            purchaseDate={formData.purchaseDate}
            onBedroomsChange={(value) => updateField('bedrooms', value)}
            onBathroomsChange={(value) => updateField('bathrooms', value)}
            onSquareFootageChange={(value) => updateField('squareFootage', value)}
            onYearBuiltChange={(value) => updateField('yearBuilt', value)}
            onPurchaseDateChange={(value) => updateField('purchaseDate', value)}
            enrichingHomeData={enrichingHomeData}
            enrichmentError={enrichmentError}
            progressMessages={progressMessages}
            currentProgressMessage={currentProgressMessage}
            fadeAnim={fadeAnim}
          />
        );

      case 3:
        return (
          <InterestsStep
            interests={formData.interests}
            onInterestToggle={handleInterestToggle}
          />
        );

      case 4:
        return (
          <AccountCreationStep
            fullName={formData.fullName}
            email={formData.email}
            password={formData.password}
            phoneNumber={formData.phoneNumber}
            acceptedTerms={formData.acceptedTerms}
            onFullNameChange={(value) => updateField('fullName', value)}
            onEmailChange={(value) => updateField('email', value)}
            onPasswordChange={(value) => updateField('password', value)}
            onPhoneNumberChange={(value) => updateField('phoneNumber', value)}
            onAcceptedTermsChange={(value) => updateField('acceptedTerms', value)}
          />
        );

      case 5:
        return <SuccessStep referralClaimFailed={referralClaimFailed} />;

      default:
        return null;
    }
  };

  const handleContinue = async () => {
    if (currentStep === 1) {
      // Validate address before proceeding
      if (!selectedPlace && !formData.address.trim()) {
        setError('Please enter and select your street address');
        return;
      }
      if (!selectedPlace) {
        setError('Please select an address from the suggestions');
        return;
      }
      // Proceed to home details step
      setCurrentStep(prev => prev + 1);
      return;
    }
    if (currentStep === 2) {
      // Update home with user-entered data and proceed to next step
      try {
        setLoading(true);
        setError('');
        await updateHomeForOnboarding();
        setError('');
        setCurrentStep(prev => prev + 1);
      } catch (err: any) {
        console.error('Error updating home:', err);
        setError(err.message || 'Failed to save home details. Please try again.');
      } finally {
        setLoading(false);
      }
      return;
    }
    if (currentStep === 4) {
      handleSubmit();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const canContinue = currentStep === 1 ? !!selectedPlace : true;
  const continueButtonText = currentStep === 4 ? 'Create Account' : 'Continue';
  const loadingText = currentStep === 1 ? 'Saving Address...' : currentStep === 4 ? 'Creating Account...' : '';

  return (
    <View style={styles.container}>
      {Platform.OS === 'web' &&
        createElement('div', {
          'aria-hidden': true,
          style: {
            position: 'absolute',
            top: -48,
            left: -48,
            right: -48,
            bottom: -48,
            zIndex: 0,
            pointerEvents: 'none',
            backgroundImage: `url(${heroBackground})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: 'blur(20px)',
          },
        })}
      {Platform.OS === 'web' && (
        <View style={styles.backgroundScrim} pointerEvents="none" />
      )}
      <WebScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <View style={styles.content}>
          {/* Referral Banner */}
          {referralValid && referrerName && (
            <View style={styles.referralBanner}>
              <Text style={styles.referralBannerTitle}>
                You've been invited by {referrerName}!
              </Text>
              <Text style={styles.referralBannerText}>
                Complete your signup to receive {formatCentsToUSDShort(REFERRAL_CONFIG.NEW_MEMBER_CREDIT_CENTS)} in credits.
              </Text>
            </View>
          )}
          {householdInviteToken && (
            <View style={styles.householdBanner}>
              <Text style={styles.householdBannerTitle}>Household Invite</Text>
              <Text style={styles.householdBannerText}>
                Complete signup to join a shared HoneyDone home.
              </Text>
            </View>
          )}
          {referringAgent && (
            <View style={styles.agentBanner}>
              <Text style={styles.agentBannerTitle}>
                Referred by {referringAgent.fullName}
                {referringAgent.brokerage ? ` • ${referringAgent.brokerage}` : ''}
              </Text>
              <Text style={styles.agentBannerText}>
                {referringAgent.fullName.split(' ')[0]} will appear on your "Your Team" page so you can stay in touch after closing.
              </Text>
            </View>
          )}
          
          {currentStep < 5 ? (
            <ProgressBar currentStep={currentStep} totalSteps={5} />
          ) : null}
          <View style={styles.card}>
            {renderStep() || null}
            {error && (
              <Text style={styles.errorText}>{error}</Text>
            )}
            {currentStep < 5 && !(currentStep === 1 && !selectedPlace) && (
              <NavigationButtons
                currentStep={currentStep}
                loading={loading}
                onBack={() => setCurrentStep(prev => prev - 1)}
                onContinue={handleContinue}
                canContinue={canContinue}
                continueButtonText={continueButtonText}
                loadingText={loadingText}
              />
            )}
          </View>
          {currentStep < 5 && (
            <View style={styles.footer}>
              <Text style={styles.footerText}>
                Already have an account?{' '}
                <Link to={householdInviteToken ? `/login?invite=${householdInviteToken}` : '/login'} style={styles.footerLink}>
                  <Text style={styles.footerLinkText}>Sign in</Text>
                </Link>
              </Text>
            </View>
          )}
          {currentStep > 0 && currentStep < 5 && (
            <View style={styles.restartContainer}>
              <Pressable onPress={handleRestart}>
                <Text style={styles.restartText}>Start over</Text>
              </Pressable>
            </View>
          )}
        </View>
      </WebScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: Platform.OS === 'web' ? 'var(--app-height)' as any : '100%',
    backgroundColor: '#f9fafb',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 48,
    paddingHorizontal: 16,
    ...(Platform.OS === 'web' && {
      paddingHorizontal: 24,
      position: 'relative' as const,
      overflow: 'hidden' as const,
    }),
  },
  backgroundScrim: {
    ...(Platform.OS === 'web' && {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 0,
      backgroundColor: 'rgba(249, 250, 251, 0.55)',
    }),
  },
  scrollView: {
    flex: 1,
    width: '100%',
    ...(Platform.OS === 'web' && {
      position: 'relative' as const,
      zIndex: 1,
    }),
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    width: '100%',
    maxWidth: 768,
    alignSelf: 'center',
    ...(Platform.OS === 'web' && {
      marginHorizontal: 'auto' as any,
    }),
  },
  referralBanner: {
    backgroundColor: '#7c3aed',
    borderRadius: 12,
    padding: 20,
    marginBottom: 24,
    ...(Platform.OS === 'web' && shadows.md),
  },
  referralBannerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: 4,
  },
  referralBannerText: {
    fontSize: 14,
    color: '#f3e8ff',
  },
  householdBanner: {
    backgroundColor: '#111827',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    ...(Platform.OS === 'web' && shadows.md),
  },
  householdBannerTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: 2,
  },
  householdBannerText: {
    fontSize: 13,
    color: '#d1d5db',
  },
  agentBanner: {
    backgroundColor: '#0f766e',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    ...(Platform.OS === 'web' && shadows.md),
  },
  agentBannerTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: 2,
  },
  agentBannerText: {
    fontSize: 13,
    color: '#ccfbf1',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    padding: 32,
    ...(Platform.OS === 'web' && shadows.sm),
  },
  errorText: {
    color: '#ef4444',
    fontSize: 14,
    textAlign: 'center',
    marginTop: 16,
  },
  footer: {
    marginTop: 16,
  },
  footerText: {
    fontSize: 14,
    color: '#4b5563',
    textAlign: 'center',
  },
  footerLink: {
    textDecorationLine: 'none',
  },
  footerLinkText: {
    fontWeight: '500',
    color: '#000000',
    ...(Platform.OS === 'web' && {
      textDecorationLine: 'underline',
    }),
  },
  restartContainer: {
    marginTop: 24,
    alignItems: 'center',
  },
  restartText: {
    fontSize: 12,
    color: '#9ca3af',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      textDecorationLine: 'underline',
    }),
  },
});

export default Onboarding;
